import React, { FC, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Dropdown, Input, Modal, Row, Tag, Tree } from 'antd';
import { AntTreeNodeProps, DataNode } from 'antd/es/tree';
import axios from 'axios';
import { LucideListEnd } from 'lucide-react';
import { TbArrowRight, TbChevronDown, TbLockOff } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownMenuPanel, EmptySection, LearnMoreFooter, OverflowFadeScrollArea } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { PlatformForm as Form } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import WorkspaceResourceAvatar from '@/shared/components/workspace-resources/WorkspaceResourceAvatar';
import { APIKey, APIKeyTimeLength, SetStateMethod } from '@/types';
import { dayjs } from '@/utilities';

interface CreateAPIKeyModalProps {
    onCreated?: (apiKey: APIKey) => void;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const apiKeyResources = [
    ['folders', 'Folders'],
    ['investments', 'Investments'],
    ['memos', 'Memos'],
    ['pipelines', 'Pipelines'],
    ['dashboards', 'Dashboards'],
    ['lists', 'Lists'],
    ['reports', 'Reports'],
    ['tasks', 'Tasks'],
    ['teams', 'Teams'],
] as const;

const apiKeyActions = [
    ['read', 'Read', 'GET'],
    ['create', 'Create', 'POST'],
    ['update', 'Update', 'PUT'],
    ['delete', 'Delete', 'DELETE'],
] as const;

const PermissionNode: FC<{ label: string; method: string }> = ({ label, method }) => {
    const colorMap: Record<string, string> = { GET: 'green', POST: 'blue', PUT: 'orange', DELETE: 'red' };

    return (
        <span>
            {label}{' '}
            <Tag color={colorMap[method]} bordered={false}>
                {method}
            </Tag>
        </span>
    );
};

interface APIKeyResourceNode extends DataNode {
    searchLabel: string;
    children: Array<DataNode & { key: string }>;
}

const CreateAPIKeyModal: FC<CreateAPIKeyModalProps> = ({ onCreated, open, setOpen }): JSX.Element => {
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState<string>('');
    const [timeLength, setTimeLength] = useState<APIKeyTimeLength>(APIKeyTimeLength.ONE_WEEK);
    const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const { token } = useAntDesignTheme();

    function getFormattedDate(option: APIKeyTimeLength) {
        switch (option) {
            case APIKeyTimeLength.ONE_WEEK:
                return dayjs().add(1, 'week').format('MMMM Do, YYYY');
            case APIKeyTimeLength.THIRTY_DAYS:
                return dayjs().add(30, 'days').format('MMMM Do, YYYY');
            case APIKeyTimeLength.THREE_MONTHS:
                return dayjs().add(3, 'months').format('MMMM Do, YYYY');
            case APIKeyTimeLength.ONE_YEAR:
                return dayjs().add(1, 'year').format('MMMM Do, YYYY');
            case APIKeyTimeLength.NEVER_EXPIRES:
                return '';
            default:
                captureException('Invalid input');
                return '';
        }
    }

    const handleCreateAPIKey = async () => {
        if (!name.trim() || selectedPermissions.length === 0) {
            return;
        }

        try {
            setCreating(true);
            const { data } = await authorizedAxiosInstance.post<APIKey>('/api/apikeys', {
                name: name.trim(),
                timeLength,
                resources: selectedPermissions,
            });

            onCreated?.(data);
            setName('');
            setCheckedKeys([]);
            setSearchValue('');
            setTimeLength(APIKeyTimeLength.ONE_WEEK);
            setOpen(false);
        } catch (error) {
            captureException(error);
            toast.error('Unable to create API key', {
                description: axios.isAxiosError(error)
                    ? error.response?.data?.error?.message || 'Please verify the key configuration and try again.'
                    : 'Please try again later.',
            });
        } finally {
            setCreating(false);
        }
    };

    const resourceData = useMemo<APIKeyResourceNode[]>(
        () =>
            apiKeyResources.map(([resource, label]) => ({
                key: resource,
                searchLabel: label,
                title: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                        <WorkspaceResourceAvatar resource={resource} size={24} />
                        <span>{label}</span>
                    </span>
                ),
                children: apiKeyActions.map(([action, actionLabel, method]) => ({
                    key: `${resource}:${action}`,
                    title: <PermissionNode label={actionLabel} method={method} />,
                })),
            })),
        [],
    );

    const filteredTreeData = useMemo(() => {
        if (!searchValue) {
            return resourceData;
        }

        const lower = searchValue.toLowerCase();

        return resourceData
            .map((node) => {
                const match =
                    node.searchLabel.toLowerCase().includes(lower) ||
                    node.children?.some((child) => child.key.toLowerCase().includes(lower));
                if (!match) {
                    return null;
                }
                return {
                    ...node,
                    children: node.children?.filter((child) => child.key.toLowerCase().includes(lower)),
                };
            })
            .filter((node): node is APIKeyResourceNode => node !== null);
    }, [searchValue, resourceData]);

    const permissionKeys = useMemo(
        () => new Set(resourceData.flatMap(({ children }) => children.map(({ key }) => key))),
        [resourceData],
    );
    const selectedPermissions = useMemo(
        () => checkedKeys.map(String).filter((key) => permissionKeys.has(key)),
        [checkedKeys, permissionKeys],
    );
    const disabled = !name.trim() || selectedPermissions.length === 0;

    const close = () => {
        setName('');
        setCheckedKeys([]);
        setSearchValue('');
        setTimeLength(APIKeyTimeLength.ONE_WEEK);
        setOpen(false);
    };

    return (
        <Modal
            footer={
                <Row align='middle' justify='space-between' wrap={false}>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <LearnMoreFooter
                            align='left'
                            href='https://docs.countercyclical.io/developers/getting-started'
                            topic='our API'
                        />
                    </Col>
                    <Col flex='none'>
                        <Button disabled={creating} onClick={close}>
                            Cancel
                        </Button>
                        <Button
                            disabled={disabled}
                            loading={creating}
                            style={{ marginLeft: token.marginXS }}
                            type='primary'
                            onClick={() => void handleCreateAPIKey()}
                        >
                            Create API Key
                            <TbArrowRight />
                        </Button>
                    </Col>
                </Row>
            }
            open={open}
            mask={false}
            title='Create API Key'
            onCancel={close}
            onOk={handleCreateAPIKey}
            width={'40vw'}
        >
            <Form layout='vertical' requiredMark={false}>
                <Row gutter={16}>
                    <Col span={16}>
                        <Form.Item label='Name' style={{ marginBottom: 0 }}>
                            <Input
                                placeholder='Key Name...'
                                value={name}
                                maxLength={250}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            style={{ marginBottom: 0 }}
                            label={
                                <>
                                    Expiration Date
                                    {timeLength !== APIKeyTimeLength.NEVER_EXPIRES && (
                                        <>
                                            <TbArrowRight style={{ margin: '0 0.5rem', verticalAlign: '-1px' }} />
                                            <span style={{ fontWeight: 500 }}>{getFormattedDate(timeLength)}</span>
                                        </>
                                    )}
                                </>
                            }
                        >
                            <Dropdown
                                trigger={['click']}
                                popupRender={() => (
                                    <DropdownMenuPanel
                                        {...{
                                            selectedKeys: [timeLength],
                                            onClick: ({ key }) => setTimeLength(key as APIKeyTimeLength),
                                            items: [
                                                { key: APIKeyTimeLength.ONE_WEEK, label: '1 Week' },
                                                { key: APIKeyTimeLength.THIRTY_DAYS, label: '30 Days' },
                                                { key: APIKeyTimeLength.THREE_MONTHS, label: '3 Months' },
                                                { key: APIKeyTimeLength.ONE_YEAR, label: '1 Year' },
                                                { type: 'divider' },
                                                { key: APIKeyTimeLength.NEVER_EXPIRES, label: 'Never Expire' },
                                            ],
                                        }}
                                    />
                                )}
                            >
                                <DropdownButton block>
                                    <span
                                        style={{
                                            width: '100%',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <span>{timeLength}</span>
                                    </span>
                                </DropdownButton>
                            </Dropdown>
                        </Form.Item>
                    </Col>
                </Row>
                <Divider style={{ margin: `${token.margin}px 0` }} orientation='left'>
                    Permissions
                </Divider>
                <Input.Search
                    placeholder='Search Resources...'
                    allowClear
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    style={{ marginBottom: 12 }}
                />
                <Row justify='space-between' style={{ marginBottom: 8 }}>
                    <Col flex='auto'>
                        <small style={{ color: token.colorTextTertiary }}>
                            <LucideListEnd
                                size='0.75rem'
                                style={{
                                    transform: 'rotateY(180deg)',
                                    verticalAlign: '-0.125rem',
                                    marginRight: 6,
                                }}
                            />
                            Results
                        </small>
                    </Col>
                    {/* <Col flex='auto' /> */}
                    {/* <Col flex='none'>
                        <Tag color='blue' style={{ borderColor: 'transparent', marginRight: 0 }}>
                            <TbSquareCheckFilled style={{ verticalAlign: '-0.1rem', marginRight: 4 }} />
                            {checkedKeys.length} Methods Selected
                        </Tag>
                    </Col> */}
                </Row>
                <OverflowFadeScrollArea
                    aria-label='API key resource policies'
                    containerStyle={{
                        maxHeight: 300,
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                    }}
                    style={{ padding: '0.75rem 0.5rem' }}
                    surface='elevated'
                >
                    {filteredTreeData.length ? (
                        <Tree
                            checkable
                            defaultExpandAll
                            switcherIcon={(props: AntTreeNodeProps) => {
                                return (
                                    <TbChevronDown
                                        style={{
                                            transform: props.expanded ? undefined : 'rotate(-90deg)',
                                            color: token.colorTextTertiary,
                                        }}
                                    />
                                );
                            }}
                            showLine
                            selectable={false}
                            checkedKeys={checkedKeys}
                            onCheck={(keys) => setCheckedKeys(keys as React.Key[])}
                            treeData={filteredTreeData}
                        />
                    ) : (
                        <EmptySection
                            height={16 * 10}
                            icon={<TbLockOff size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                            label='No Matching Resources'
                            description='Adjust your search to find resources this API key can access.'
                        />
                    )}
                </OverflowFadeScrollArea>
            </Form>
        </Modal>
    );
};

export default CreateAPIKeyModal;
